import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Stack,
  Typography,
  Checkbox,
} from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import axios from 'axios';

const CoachUnlinkDialog = ({ onClose, linkedCoaches, onRefresh }) => {
  const [selectedCoaches, setSelectedCoaches] = useState(new Set()); // Set of coach_ids to unlink

  const handleToggleCoach = (coachId) => {
    setSelectedCoaches((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(coachId)) {
        newSet.delete(coachId);
      } else {
        newSet.add(coachId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allCoachIds = linkedCoaches.map((coach) => coach.coach_id);
    setSelectedCoaches(new Set(allCoachIds));
  };

  const handleDeselectAll = () => {
    setSelectedCoaches(new Set());
  };

  const handleSubmit = async () => {
    if (selectedCoaches.size === 0) {
      onClose();
      return;
    }

    // Make individual DELETE requests for each selected coach
    const deletePromises = Array.from(selectedCoaches).map((coachId) =>
      axios.delete(`${process.env.REACT_APP_API_HOST}/api/linked-coaches/${coachId}`)
    );

    try {
      await Promise.all(deletePromises);
      // Refresh the linked coaches list
      if (onRefresh) {
        onRefresh();
      }
      onClose();
    } catch (err) {
      console.log(err);
    }
  };

  const isCoachSelected = (coachId) => selectedCoaches.has(coachId);
  const hasSelection = selectedCoaches.size > 0;
  const allSelected = selectedCoaches.size === linkedCoaches.length;

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Unlink Coaches</DialogTitle>
      <DialogContent dividers>
        {linkedCoaches.length === 0 ? (
          <Typography>No linked coaches.</Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select coaches to unlink from. They will no longer have access to your data.
            </Typography>
            <List>
              {linkedCoaches.map((coach) => (
                <ListItem key={coach.coach_id} divider>
                  <Checkbox
                    checked={isCoachSelected(coach.coach_id)}
                    onChange={() => handleToggleCoach(coach.coach_id)}
                    sx={{ mr: 1 }}
                  />
                  <ListItemText
                    primary={coach.coach_name}
                    //secondary={coach.gym_name || 'Independent Coach'}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      color={isCoachSelected(coach.coach_id) ? 'error' : 'default'}
                      onClick={() => handleToggleCoach(coach.coach_id)}
                    >
                      <LinkOffIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1}>
          <Button 
            onClick={allSelected ? handleDeselectAll : handleSelectAll}
            disabled={linkedCoaches.length === 0}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="error"
            disabled={!hasSelection}
          >
            Unlink Selected ({selectedCoaches.size})
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default CoachUnlinkDialog;